import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { Edit, Trash } from 'react-feather'
import {
  Box,
  Button,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import {
  CreatePricingModelInput,
  PricingModelFragment,
  UpdatePricingModelInput,
  useGlobalPricingModelsQuery,
} from '../../generated/graphql'
import { Loader } from '../common/Loader'
import { usePricingModels } from '../../pricing-model/use-pricing-models'
import {
  mapFormValuesToInput,
  mapPricingModelToValues,
  PricingModelDialog,
  PricingModelFormValues,
} from '../pricing-models/PricingModelDialog'
import { useOnDeletePricingModel } from '../../pricing-model/use-on-delete-pricing-model'

export function GlobalPricingModelsPage(): React.ReactElement {
  const { t } = useTranslation()
  const [pricingModels, setPricingModels] = useState<PricingModelFragment[]>([])
  const [editingPricingModel, setEditingPricingModel] =
    useState<PricingModelFragment | null>(null)
  const [isCreatingPricingModel, setIsCreatingPricingModel] = useState(false)
  const {
    createGlobalPricingModel,
    updatePricingModel,
    isLoading: isChangeLoading,
  } = usePricingModels()
  const { onDeletePricingModel } = useOnDeletePricingModel()
  const { data, loading: isListLoading } = useGlobalPricingModelsQuery()

  useEffect(() => {
    setPricingModels(data?.globalPricingModels ?? [])
  }, [data])

  const onCreate = async (values: PricingModelFormValues) => {
    const input = mapFormValuesToInput<CreatePricingModelInput>(values)
    await createGlobalPricingModel(input)

    setIsCreatingPricingModel(false)
  }

  const onEdit = async (values: PricingModelFormValues) => {
    if (!editingPricingModel) {
      return
    }

    const input = mapFormValuesToInput<UpdatePricingModelInput>(values)
    await updatePricingModel(editingPricingModel.id, input)

    setEditingPricingModel(null)
  }

  const isCreateDisabled = isListLoading || isChangeLoading

  return (
    <Box>
      <Helmet title={t('globalPricingModels.header')} />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py={4}
      >
        <Typography variant="h3">{t('globalPricingModels.header')}</Typography>

        <Button
          id="add-global-pricing-model-button"
          variant="contained"
          color="primary"
          disabled={isCreateDisabled}
          onClick={() => setIsCreatingPricingModel(true)}
        >
          {t('globalPricingModels.createNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="90%">
                {t('globalPricingModels.tableHeaderName')}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isListLoading ? (
              <Box height={300}>
                <Loader />
              </Box>
            ) : (
              pricingModels.map((pricingModel) => (
                <TableRow key={pricingModel.id}>
                  <TableCell>{pricingModel.name}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          setEditingPricingModel(pricingModel)
                        }}
                      >
                        <Icon component={Edit} size="small" />
                      </IconButton>
                      <Box mr={1} />
                      <IconButton
                        data-cy={`delete-${pricingModel.name}`}
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          onDeletePricingModel(pricingModel)
                        }}
                      >
                        <Icon component={Trash} size="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PricingModelDialog
        isOpen={isCreatingPricingModel}
        isCreate
        defaultValues={mapPricingModelToValues(null)}
        onSubmit={onCreate}
        onClose={() => setIsCreatingPricingModel(false)}
      />

      <PricingModelDialog
        isOpen={editingPricingModel !== null}
        defaultValues={mapPricingModelToValues(editingPricingModel)}
        onSubmit={onEdit}
        onClose={() => setEditingPricingModel(null)}
      />
    </Box>
  )
}
